import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Injectable, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  OperatorFunction,
  startWith,
  Subject,
  tap,
} from 'rxjs';
import { addDoc, collection } from 'firebase/firestore';
import { FIRESTORE } from '../shared/firebase/firebase.tokens';
import { ToastService } from '../shared/toasts/toast.service';
import { ButtonComponent } from '../shared/button/button.component';
import { CARDS } from '../shared/card';
import { SECTIONS } from '../shared/section';
import { BookingOfferType, getOffer } from '../shared/data/offers.data';
import {
  NgbCalendar,
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerModule,
  NgbDateStruct,
  NgbTypeahead,
  NgbTypeaheadModule,
} from '@ng-bootstrap/ng-bootstrap';

type Duration = 1 | 1.5;
type OfferType = BookingOfferType;
type UnaccustomedOfferType = Exclude<OfferType, 'Betreut'>;

function buildDurationPrice(): Record<Duration, number> {
  const betreut = getOffer('betreut');

  const result = {} as Record<Duration, number>;

  for (const price of betreut.prices) {
    if (price.duration === 1 || price.duration === 1.5) {
      result[price.duration as Duration] = price.amount;
    }
  }

  return result;
}

function buildOfferTypeDayPrice(): Record<UnaccustomedOfferType, number> {
  return {
    'Nicht Betreut': getOffer('unbetreut').prices[0].amount,
    'Kinder (Unbetreut)': getOffer('kids').prices[0].amount,
  };
}

/**
 * This Service handles how the date is represented in scripts i.e. ngModel.
 */
@Injectable()
export class CustomAdapter extends NgbDateAdapter<string> {
  readonly DELIMITER = '.';

  fromModel(value: string | null): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.DELIMITER);
      return {
        day: Number.parseInt(date[0], 10),
        month: Number.parseInt(date[1], 10),
        year: Number.parseInt(date[2], 10),
      };
    }
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  toModel(date: NgbDateStruct | null): string | null {
    return date
      ? String(date.day).padStart(2, '0') +
          this.DELIMITER +
          String(date.month).padStart(2, '0') +
          this.DELIMITER +
          date.year
      : // eslint-disable-next-line unicorn/no-null
        null;
  }
}

function getQuarterHourTimes(): string[] {
  const times: string[] = [];

  for (let hours = 0; hours < 24; hours++) {
    for (let minutes = 0; minutes < 60; minutes += 15) {
      const time = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      times.push(time);
    }
  }

  return times;
}

/**
 * This Service handles how the date is rendered and parsed from keyboard i.e. in the bound input field.
 */
@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = '.';

  parse(value: string): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.DELIMITER);
      return {
        day: Number.parseInt(date[0], 10),
        month: Number.parseInt(date[1], 10),
        year: Number.parseInt(date[2], 10),
      };
    }
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  format(date: NgbDateStruct | null): string {
    return date
      ? String(date.day).padStart(2, '0') +
          this.DELIMITER +
          String(date.month).padStart(2, '0') +
          this.DELIMITER +
          date.year
      : '';
  }
}

@Component({
  selector: 'lumiron-booking',
  templateUrl: './booking.component.html',
  styleUrl: './booking.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    SECTIONS,
    CARDS,
    ButtonComponent,
    NgbDatepickerModule,
    NgbTypeaheadModule,
  ],
  providers: [
    { provide: NgbDateAdapter, useClass: CustomAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
})
export class BookingComponent {
  private readonly firestore = inject(FIRESTORE);
  private readonly toastService = inject(ToastService);
  private readonly ngbCalendar = inject(NgbCalendar);

  protected readonly times = getQuarterHourTimes();
  protected readonly minDate = this.ngbCalendar.getToday();

  public readonly durationPrice: Record<Duration, number> = buildDurationPrice();

  protected readonly offerTypeDayPrice: Record<UnaccustomedOfferType, number> = buildOfferTypeDayPrice();

  private readonly durationHoursInitValue: Duration = 1;
  private readonly durationDaysInitValue = 1;

  public readonly offerForm: FormGroup = new FormGroup({
    firstname: new FormControl<string>('', Validators.required),
    lastname: new FormControl<string>('', Validators.required),
    email: new FormControl<string>('', [Validators.required, Validators.email]),
    phone: new FormControl<string | undefined>(undefined),
    offerType: new FormControl<OfferType>('Betreut', {
      nonNullable: true,
      validators: Validators.required,
    }),
    date: new FormControl<Date | undefined>(undefined, Validators.required),
    time: new FormControl<string | undefined>(undefined, Validators.required),
    field: new FormControl<'Eigener Platz' | 'Kein eigener Platz'>('Eigener Platz', {
      nonNullable: true,
      validators: Validators.required,
    }),
    durationHours: new FormControl<Duration>(this.durationHoursInitValue, {
      nonNullable: true,
      validators: Validators.required,
    }),
    durationDays: new FormControl<number>(1, {
      nonNullable: true,
      validators: Validators.required,
    }),
    total: new FormControl<string>(
      { value: `${getOffer('betreut').prices[0].amount} CHF`, disabled: true },
      { nonNullable: true, validators: Validators.required },
    ),
    message: new FormControl<string | undefined>(undefined),
    agb: new FormControl<boolean>(false, Validators.requiredTrue),
  });

  protected readonly showDurationHours$ = this.offerForm.controls['offerType'].valueChanges.pipe(
    startWith('Betreut'),
    map((ot: OfferType) => ot == 'Betreut'),
    tap(showDurationHours => {
      if (showDurationHours) {
        this.offerForm.controls['durationHours'].addValidators(Validators.required);
        this.offerForm.controls['durationDays'].removeValidators(Validators.required);
        this.offerForm.controls['field'].enable();
        this.offerForm.controls['durationHours'].setValue(this.durationHoursInitValue);
      } else {
        this.offerForm.controls['durationDays'].addValidators(Validators.required);
        this.offerForm.controls['durationHours'].removeValidators(Validators.required);
        this.offerForm.controls['field'].disable();
        this.offerForm.controls['durationDays'].setValue(this.durationDaysInitValue);
      }
    }),
  );

  protected readonly sendingMessage = signal(false);

  protected readonly timeTypeahead = viewChild<NgbTypeahead>('timeTypeahead');
  protected readonly focus$ = new Subject<string>();
  protected readonly click$ = new Subject<string>();

  public search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.timeTypeahead()?.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map(term => (term === '' ? this.times : this.times.filter(v => v.toLowerCase().includes(term.toLowerCase())))),
    );
  };

  constructor() {
    this.offerForm.controls['durationHours'].valueChanges
      .pipe(
        map((d: Duration) => this.durationPrice[d] + ' CHF'),
        tap(p => this.offerForm.controls['total'].setValue(p)),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.offerForm.controls['durationDays'].valueChanges
      .pipe(
        map(
          d => d * this.offerTypeDayPrice[this.offerForm.controls['offerType'].value as UnaccustomedOfferType] + ' CHF',
        ),
        tap(p => this.offerForm.controls['total'].setValue(p)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  protected sendOfferForm(): void {
    if (!this.offerForm.valid) {
      return;
    }

    const formValue = this.offerForm.getRawValue();
    if (this.offerForm.value.offerType == 'Betreut') {
      delete formValue.durationDays;
    } else {
      delete formValue.durationHours;
    }

    this.offerForm.disable();
    this.sendingMessage.set(true);

    addDoc(collection(this.firestore, 'offerForms'), { ...formValue, state: 'Angefragt' })
      .catch(() => {
        this.sendingMessage.set(false);
        this.toastService.showToast('error', 'Es ist ein Fehler aufgetreten.');
        this.offerForm.enable();
      })
      .finally(() => {
        this.sendingMessage.set(false);
        this.toastService.showToast('success', 'Anfrage gesendet.');
        this.offerForm.reset();
        this.offerForm.enable();
        this.offerForm.controls['total'].disable();
      });
  }
}
